import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * ZION : ajout au panier AJAX branché sur le bus d'événements Horizon.
 *
 * zionAddItems dispatch un CartLinesUpdateEvent (action 'add') puis résout sa
 * promesse avec { cart, detail } exactement comme product-form.js du thème.
 * Les composants natifs font le reste :
 *  - cart-drawer-component  -> ouvre le drawer (attribut auto-open)
 *  - cart-items-component   -> morph du contenu du drawer (detail.sections)
 *  - cart-icon              -> bulle compteur du header (cart.totalQuantity)
 *  - header-actions         -> annonce lecteurs d'écran
 *
 * Quatre points d'entrée :
 *  1. form[data-zion-ajax-add]  : fiche produit (submit intercepté, fallback natif)
 *  2. [data-zion-add-variant]   : boutons d'ajout 1 clic (encart duo du drawer),
 *                                 délégué au document pour survivre aux morphs
 *  3. window.zionAddItems       : appels depuis les scripts non-module (zion-duo)
 *  4. Encart duo du drawer      : onglets modèle + vignettes coloris (délégation)
 *  5. Bouton Commander          : tout départ vers /checkout est redirigé vers le
 *                                 checkout COD maison (Vercel) avec le panier en URL
 */

const routes = (window.Theme && window.Theme.routes) || {};
const CART_ADD_URL = routes.cart_add_url || '/cart/add';
const CART_URL = routes.cart_url || '/cart';

function sectionsToUpdate() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids).join(',');
}

async function fetchCart() {
  const response = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`cart ${response.status}`);
  return response.json();
}

/**
 * @param {Array<{id: number, quantity: number}>} items - variantes à ajouter
 * @param {Element} [sourceEl] - élément d'origine (cible du dispatch, doit être dans le DOM)
 * @returns {Promise<Object>} le panier /cart.js après ajout
 */
export async function zionAddItems(items, sourceEl) {
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();
  const target = sourceEl instanceof Element && sourceEl.isConnected ? sourceEl : document.body;

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity })),
        sections: sectionsToUpdate(),
        sections_url: window.location.pathname,
      }),
    }).then((r) => r.json());

    const cart = await fetchCart();

    if (response.status) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'zion-cart', itemCount: totalQuantity },
      });
      const error = new Error(response.description || response.message || 'Ajout au panier impossible');
      error.zionCartError = true;
      throw error;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'zion-cart',
        itemCount: totalQuantity,
        sections: response.sections,
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    if (!error || error.zionCartError !== true) deferred.reject(error);
    throw error;
  }
}

/* 1. Fiche produit : submit intercepté, soumission native en secours. */
document.addEventListener('submit', (event) => {
  const form = event.target instanceof Element ? event.target.closest('form[data-zion-ajax-add]') : null;
  if (!form) return;

  const idInput = form.querySelector('input[name="id"]');
  const variantId = Number(idInput && idInput.value);
  if (!variantId) return; /* données inattendues : on laisse le navigateur soumettre */

  event.preventDefault();

  const quantityInput = form.querySelector('[name="quantity"]');
  const quantity = Math.max(1, Number(quantityInput && quantityInput.value) || 1);
  const scope = form.closest('section') || form;
  const ctas = scope.querySelectorAll('[data-zion-cta]');
  const errorEl = scope.querySelector('[data-zion-cart-error]');

  if (errorEl) errorEl.hidden = true;
  for (const cta of ctas) {
    cta.disabled = true;
    cta.setAttribute('aria-busy', 'true');
  }

  zionAddItems([{ id: variantId, quantity }], form)
    .catch((error) => {
      if (error && error.zionCartError) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
      } else {
        /* panne JS ou réseau : on ne bloque jamais l'achat */
        form.submit();
      }
    })
    .finally(() => {
      for (const cta of ctas) {
        cta.disabled = false;
        cta.removeAttribute('aria-busy');
      }
    });
});

/* 2. Boutons d'ajout 1 clic (encart duo du drawer), délégation résistante aux morphs. */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-zion-add-variant]') : null;
  if (!button || button.disabled) return;

  const variantId = Number(button.dataset.zionAddVariant);
  if (!variantId) return;

  event.preventDefault();
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  zionAddItems([{ id: variantId, quantity: 1 }], button).catch(() => {
    /* en cas de succès, le morph du drawer retire le bouton lui-même */
    button.disabled = false;
    button.removeAttribute('aria-busy');
  });
});

/* 3. Pont pour les scripts non-module des sections (zion-duo). */
window.zionAddItems = zionAddItems;

/* 5. Checkout maison : le bouton Commander (drawer et page /cart) part vers le
   checkout paiement-à-la-livraison avec les lignes du panier en paramètre.
   Écoute en capture pour devancer le submit natif du form cart ; si le fetch du
   panier échoue, soumission native en secours (checkout Shopify, jamais bloqué). */
const ZION_CHECKOUT_URL = 'https://checkout.zioneyewear.com/';

document.addEventListener(
  'click',
  (event) => {
    const cible = event.target instanceof Element
      ? event.target.closest('button[name="checkout"], a[href^="/checkout"]')
      : null;
    if (!cible || cible.dataset.zionCheckoutEnCours) return;

    event.preventDefault();
    event.stopPropagation();
    cible.dataset.zionCheckoutEnCours = '1';
    cible.setAttribute('aria-busy', 'true');

    fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const lignes = (cart.items || [])
          .map((item) => `${item.variant_id}:${item.quantity}`)
          .join(',');
        /* Code de bienvenue en cours : il voyage avec le panier, sinon la
           remise serait perdue au passage sur le checkout maison. */
        const promo = window.zionPromo ? window.zionPromo.parametre() : '';
        window.location.href = lignes
          ? `${ZION_CHECKOUT_URL}?panier=${encodeURIComponent(lignes)}${promo}`
          : ZION_CHECKOUT_URL;
      })
      .catch(() => {
        delete cible.dataset.zionCheckoutEnCours;
        cible.removeAttribute('aria-busy');
        if (cible instanceof HTMLButtonElement && cible.form) cible.form.submit();
        else window.location.href = '/checkout';
      });
  },
  true
);

/* 4. Encart duo du drawer : onglets modèle + vignettes coloris.
   Délégation au document : l'état vit dans le DOM, il survit aux morphs. */
function zionDuoChoisitVignette(zone, vignette) {
  for (const v of zone.querySelectorAll('[data-zion-duo-vignette]')) {
    v.classList.toggle('choisie', v === vignette);
  }
  const nom = zone.querySelector('[data-zion-duo-nom]');
  if (nom) nom.textContent = vignette.dataset.nom;
  const bouton = zone.querySelector('[data-zion-add-variant]');
  if (bouton) {
    bouton.dataset.zionAddVariant = vignette.dataset.id;
    bouton.textContent = 'Ajouter · ' + vignette.dataset.prix;
  }
}

document.addEventListener('click', (event) => {
  const el = event.target instanceof Element ? event.target : null;
  if (!el) return;
  const zone = el.closest('[data-zion-drawer-duo]');
  if (!zone) return;

  const onglet = el.closest('[data-zion-duo-onglet]');
  if (onglet) {
    const groupe = onglet.dataset.zionDuoOnglet;
    for (const o of zone.querySelectorAll('[data-zion-duo-onglet]')) {
      o.classList.toggle('actif', o === onglet);
    }
    for (const g of zone.querySelectorAll('[data-zion-duo-groupe]')) {
      g.classList.toggle('cache', g.dataset.zionDuoGroupe !== groupe);
    }
    const premiere = zone.querySelector('[data-zion-duo-vignette][data-groupe="' + groupe + '"]');
    if (premiere) zionDuoChoisitVignette(zone, premiere);
    return;
  }

  const vignette = el.closest('[data-zion-duo-vignette]');
  if (vignette) zionDuoChoisitVignette(zone, vignette);
});
